/* eslint-disable @citrite/no-global-translations */
import { getFeatureFlagValue } from '@citrite/feature-flags';
import { FeatureFlag } from '../../featureFlags';
import {
	CurrencyArray,
	DateFormatArray,
	FieldsArray,
	ValidationType,
} from '../../layouts/Layouts';
import { t } from '../../utils';
import { Icons } from '../../utils/icons/icon';
import { IconStyle, LabelStyle } from './fields.styled';

export enum CurrencyEnum {
	USD = 'USD',
	EUR = 'EUR',
	GBP = 'GBP',
	AED = 'AED',
	AFN = 'AFN',
	INR = 'INR',
	AUD = 'AUD',
}

export enum DateFormatEnum {
	Date = 'MM/DD/YYYY',
	DateTime = 'MM/DD/YYYY HH:mm:ss',
	DateRange = "['MM/DD/YYYY', 'MM/DD/YYYY']",
}
export enum DateTimeFormatEnum {
	DateTime = 'MM/DD/YYYY HH:mm:ss',
	DateTime12Hrs = 'MM/DD/YYYY hh:mm:ss A',
}
// enum for all field names
export enum FieldsEnum {
	TextInputField = 'TextInputField',
	TextAreaField = 'TextAreaField',
	SingleSelectField = 'SingleSelectField',
	DateField = 'DateField',
	DateOverdueField = 'DateOverdueField',
	AccountUserMultiSelect = 'AccountUserMultiSelect',
	ToggleField = 'ToggleField',
	Select = 'Select',
	DatePickerField = 'DatePickerField',
	DropDownField = 'DropDownField',
	LinkField = 'LinkField',
	FileStorageField = 'FileStorageField',
	AssigneeField = 'AssigneeField',
	CurrencyField = 'CurrencyField',
	NumericField = 'NumericField',
	AssigneeSelectField = 'AssigneeSelectField',
	RichTextEditorField = 'RichTextEditorField',
	DateRangePicker = 'DateRangePicker',
	Status = 'Status',
	DurationField = 'DurationField',
}

export const getAllowedDateFormats = (): DateFormatArray => {
	return Object.values(DateFormatEnum) as DateFormatArray;
};

// Function to get all allowed column fields based on Fields enum
export const getAllowedColumnFields = (): FieldsArray => {
	const showStatusField = getFeatureFlagValue(FeatureFlag.enableStatusField);
	const allowedFields: FieldsArray = [
		FieldsEnum.TextInputField,
		FieldsEnum.RichTextEditorField,
		FieldsEnum.AccountUserMultiSelect,
		FieldsEnum.DateOverdueField,
		FieldsEnum.Select,
		FieldsEnum.FileStorageField,
		FieldsEnum.NumericField,
		FieldsEnum.CurrencyField,
		FieldsEnum.LinkField,
		FieldsEnum.DurationField,
	];
	if (showStatusField) {
		allowedFields.push(FieldsEnum.Status);
	}
	return allowedFields;
};

export const getAllowedCurrencies = (): CurrencyArray => {
	return Object.values(CurrencyEnum) as CurrencyArray;
};

// fields  for UI components
export const getFieldsArr = () => [
	{
		value: FieldsEnum.TextInputField,
		label: t('dynamic-components:manageField.fields.textInput.label'),
		Icon: <Icons.TextEditingTyping />,
	},
	{
		value: FieldsEnum.RichTextEditorField,
		label: t('dynamic-components:manageField.fields.textAreaField.label'),
		Icon: <Icons.TextFields />,
	},
	// Commenting below to hide text area in Add column modal SFPMGT-918
	// {
	//     value: 'TextAreaField',
	//     label: t('dynamic-components:manageField.fields.textAreaField.label'),
	//     Icon: Icons.TextFields,
	// },
	{
		value: FieldsEnum.Status,
		label: t('dynamic-components:manageField.fields.status.label'),
		Icon: <Icons.TextFields />,
	},
	{
		value: FieldsEnum.AccountUserMultiSelect,
		label: t('dynamic-components:manageField.fields.userMultiSelect.label'),
		Icon: <Icons.UserSingleNeutral />,
	},
	{
		value: FieldsEnum.DateOverdueField,
		label: t('dynamic-components:manageField.fields.dateOverdue.label'),
		Icon: <Icons.CalendarMark />,
	},
	{
		value: FieldsEnum.Select,
		label: t('dynamic-components:manageField.fields.singleSelect.label'),
		Icon: <Icons.MultiSelect />,
	},
	{
		value: FieldsEnum.FileStorageField,
		label: t('dynamic-components:manageField.fields.fileStorage.label'),
		Icon: <Icons.Paperclip />,
	},
	{
		value: FieldsEnum.NumericField,
		label: t('dynamic-components:manageField.fields.numericField.label'),
		Icon: <Icons.SignHashtag />,
	},
	{
		value: FieldsEnum.CurrencyField,
		label: t('dynamic-components:manageField.fields.currencyField.label'),
		Icon: <Icons.CoinStack />,
	},
	{
		value: FieldsEnum.LinkField,
		label: t('dynamic-components:manageField.fields.linkField.label'),
		Icon: <Icons.LinkChain />,
	},
];

export const getFields = () =>
	getFieldsArr().map((field: any) => ({
		label: (
			<LabelStyle id={field.value}>
				<IconStyle>{field.Icon}</IconStyle>
				{field.label}
			</LabelStyle>
		),
		value: field.value,
	}));

type SelectAttributeType = {
	enum: string[];
	subType?: FieldsEnum.Status;
	enumConfig: Record<
		string,
		{
			badgeType: 'default' | 'info' | 'warning' | 'success';
			displayName: string;
		}
	>;
};

type FieldAttributes =
	| { format: DateFormatEnum } // For DateField
	| { currency: CurrencyEnum } // For CurrencyField
	| SelectAttributeType; // For Select fields

type FieldProps = {
	[key in FieldsEnum | string]?: {
		validations?: ValidationType;
		attributes?: FieldAttributes;
	};
};

export const fieldProps: FieldProps = {
	TextInputField: {
		validations: {
			type: 'string',
		},
	},
	RichTextEditorField: {
		validations: {
			type: 'any',
		},
	},
	// TextAreaField: {
	//     validations: {
	//         type: 'string',
	//     },
	// },
	AccountUserMultiSelect: {
		validations: {
			type: ['string'],
		},
	},
	DateField: {
		validations: {
			type: 'date',
		},
		attributes: {
			format: DateFormatEnum.Date,
		},
	},
	DateOverdueField: {
		validations: {
			type: 'date',
		},
		attributes: {
			format: DateFormatEnum.Date,
		},
	},
	Select: {
		validations: {
			type: 'string',
		},
		attributes: {
			enum: ['Option 1', 'Option 2', 'Option 3'],
			enumConfig: {
				'Option 1': {
					badgeType: 'default',
					displayName: '',
				},
				'Option 2': {
					badgeType: 'info',
					displayName: '',
				},
				'Option 3': {
					badgeType: 'warning',
					displayName: '',
				},
			},
		},
	},
	Status: {
		validations: {
			type: 'string',
		},
		attributes: {
			subType: FieldsEnum.Status,
			enum: ['Not started', 'In-review', 'In progress', 'Completed'],
			enumConfig: {
				'Not started': {
					badgeType: 'default',
					displayName: t('dynamic-components:statusOptions.notStarted.label'),
				},
				'In-review': {
					badgeType: 'warning',
					displayName: t('dynamic-components:statusOptions.inReview.label'),
				},
				'In progress': {
					badgeType: 'info',
					displayName: t('dynamic-components:statusOptions.inProgress.label'),
				},
				Completed: {
					badgeType: 'success',
					displayName: t('dynamic-components:statusOptions.completed.label'),
				},
			},
		},
	},
	FileStorageField: {
		validations: {
			type: 'string',
		},
	},
	CurrencyField: {
		validations: {
			type: 'currency',
			decimal_places: {
				value: 2,
				message: 'Currency field cannot have more than 2 decimal places',
			},
		},

		attributes: {
			currency: CurrencyEnum.USD,
		},
	},
	NumericField: {
		validations: {
			type: 'number',
		},
	},
	LinkField: {
		validations: {
			type: 'string',
		},
	},
	DurationField: {
		validations: {
			type: 'number',
		},
	},
};

export const defaultFieldValues = {
	value: {
		label: '',
		name: '',
		component: 'TextInputField',
		validations: { type: 'string' },
		attributes: {},
	},
	isValid: true,
	isEdit: false,
};

export const RangePickerState = {
	start: 'start',
	end: 'end',
};
